import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiArrowLeft } = FiIcons;

const Disclaimer = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4" />
            <span>Back to Status Checker</span>
          </Link>
          
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiAlertTriangle} className="h-8 w-8 text-yellow-500" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              Disclaimer
            </h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Important information about the use of Web Down Status
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md space-y-6"
        >
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              General Disclaimer
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              The information provided by Web Down Status is for general informational purposes only. 
              All information on the service is provided in good faith, however we make no representation 
              or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, 
              reliability, availability, or completeness of any information on the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Accuracy of Status Reports
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
              Website status checks are performed from our servers and may not reflect the actual 
              status as experienced by all users. Results may vary due to:
            </p>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Geographic Location:</strong> Different regions may experience different accessibility
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Network Conditions:</strong> Internet connectivity and routing may affect results
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Temporary Issues:</strong> Brief outages or maintenance may not be detected
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>CORS Limitations:</strong> Browser security policies may limit available information
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              No Professional Advice
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              The information on this service is not intended as professional IT, security, or business advice. 
              You should not rely on this information as an alternative to advice from an appropriately 
              qualified professional. If you have specific questions about your website's status, security, 
              or performance, you should consult with a qualified professional.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Third-Party Services
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
              Our service relies on third-party services for some functionality:
            </p>
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Cloudflare Services</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  We use Cloudflare Workers and DNS services. Their availability and accuracy may 
                  affect our service. We are not responsible for any issues arising from third-party services.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">External APIs</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  DNS resolution and other features may rely on external APIs. Changes to these 
                  services may affect functionality without notice.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Limitation of Liability
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Under no circumstance shall Web Down Status have any liability to you for any loss or 
              damage of any kind incurred as a result of the use of the service or reliance on any 
              information provided. Your use of the service and your reliance on any information is 
              solely at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              No Warranties
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              This service is provided "as is" and "as available" without any representations or 
              warranties, express or implied. We make no warranties, expressed or implied, and 
              hereby disclaim and negate all other warranties including without limitation, implied 
              warranties or conditions of merchantability, fitness for a particular purpose, or 
              non-infringement of intellectual property or other violation of rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Indemnification
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Web Down Status and its operators 
              from and against any and all claims, damages, obligations, losses, liabilities, costs, 
              and expenses (including attorney's fees) arising from your use of the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Changes to Disclaimer
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              We reserve the right to update this disclaimer at any time without notice. By using 
              this service, you acknowledge and agree that it is your responsibility to review this 
              disclaimer periodically and become aware of modifications.
            </p>
          </section>

          <section className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h2 className="text-2xl font-semibold text-yellow-900 dark:text-yellow-100 mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiAlertTriangle} className="h-6 w-6" />
              <span>Important Notice</span>
            </h2>
            <p className="text-yellow-800 dark:text-yellow-200 leading-relaxed">
              This service should not be used as the sole method for monitoring critical infrastructure 
              or business-critical websites. For production monitoring, please use dedicated monitoring 
              services with appropriate SLAs and support.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Contact Information
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              If you have any questions about this disclaimer, please contact us at:
              <br />
              <a href="mailto:legal@webdownstatus.com" className="text-primary-600 dark:text-primary-400 hover:underline">
                legal@webdownstatus.com
              </a>
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default Disclaimer;